import React, { useMemo } from 'react';
import { Typography, Tooltip } from 'antd';
import { useTheme } from '../../theme/ThemeProvider';

const { Text } = Typography;

const MAX_USER_ROWS = 8;

function formatTime(ts) {
  return new Date(ts).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationSec(sec) {
  if (!sec || sec < 60) return `${sec || 0} сек`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  return `${h} ч ${m % 60} мин`;
}

function normalizeSessions(sessions) {
  return (sessions || [])
    .map((s, i) => ({
      id: s.id ?? i,
      username: s.username || '—',
      start: s.connected_at || s.start,
      end: s.disconnected_at || s.end,
      durationSec: s.duration_sec ?? null,
      ip: s.ip,
    }))
    .filter((s) => s.start && s.end);
}

function buildLayout(sessions, dateFrom, dateTo) {
  const from = dateFrom ? new Date(dateFrom).getTime() : null;
  const to = dateTo ? new Date(dateTo).getTime() : null;
  if (!from || !to || to <= from) return null;

  const span = to - from;
  const pct = (ts) => Math.max(0, Math.min(100, ((new Date(ts).getTime() - from) / span) * 100));

  const ticks = Array.from({ length: 6 }, (_, i) => {
    const t = from + (span * i) / 5;
    const left = (i / 5) * 100;
    return {
      left,
      label: formatTime(new Date(t)),
      align: i === 0 ? 'start' : i === 5 ? 'end' : 'center',
    };
  });

  const byUser = new Map();
  for (const s of sessions) {
    if (!byUser.has(s.username)) byUser.set(s.username, []);
    const startMs = new Date(s.start).getTime();
    const endMs = new Date(s.end).getTime();
    byUser.get(s.username).push({
      ...s,
      left: pct(s.start),
      width: Math.max(0.35, Math.min(pct(s.end) - pct(s.start), 100 - pct(s.start))),
      durationSec: s.durationSec ?? Math.round((endMs - startMs) / 1000),
    });
  }

  const userRows = [...byUser.entries()]
    .map(([username, bars]) => ({
      username,
      bars: bars.sort((a, b) => new Date(a.start) - new Date(b.start)),
      totalSec: bars.reduce((sum, b) => sum + (b.durationSec || 0), 0),
    }))
    .sort((a, b) => b.totalSec - a.totalSec);

  return { ticks, userRows, totalSessions: sessions.length };
}

const WorkSessionsTimeline = ({
  sessions = [],
  userId = null,
  username = null,
  dateFrom,
  dateTo,
  loading,
}) => {
  const { darkMode } = useTheme();
  const isSingleUser = userId != null;

  const normalized = useMemo(() => normalizeSessions(sessions), [sessions]);

  const layout = useMemo(
    () => buildLayout(normalized, dateFrom, dateTo),
    [normalized, dateFrom, dateTo]
  );

  const visibleRows = useMemo(() => {
    if (!layout) return [];
    if (isSingleUser) return layout.userRows.slice(0, 1);
    return layout.userRows.slice(0, MAX_USER_ROWS);
  }, [layout, isSingleUser]);

  const hiddenUsers = !isSingleUser && layout ? layout.userRows.length - visibleRows.length : 0;

  const totalHours = useMemo(() => {
    const sec = normalized.reduce((sum, s) => {
      if (s.durationSec != null) return sum + s.durationSec;
      return sum + (new Date(s.end) - new Date(s.start)) / 1000;
    }, 0);
    return (sec / 3600).toFixed(1);
  }, [normalized]);

  if (loading) {
    return <div className="admin-sessions-chart admin-sessions-chart--loading">Загрузка…</div>;
  }

  if (!layout || !normalized.length) {
    return <Text type="secondary">Нет сессий за выбранный период</Text>;
  }

  const hint = isSingleUser
    ? `Пользователь ${username || ''}: каждая синяя полоска — одно подключение к сервису (от открытия до закрытия вкладки).`
    : `Каждая строка — пользователь. Полоска — время, когда у него была открыта вкладка. Показаны до ${MAX_USER_ROWS} пользователей с наибольшим временем в сервисе.`;

  return (
    <div className={`admin-sessions-chart${darkMode ? ' admin-sessions-chart--dark' : ''}`}>
      <Text type="secondary" className="admin-sessions-chart__caption">
        {hint}
      </Text>

      <div className="admin-sessions-chart__axis">
        {layout.ticks.map((tick, i) => (
          <span
            key={`${tick.label}-${i}`}
            className={`admin-sessions-chart__tick admin-sessions-chart__tick--${tick.align}`}
            style={{ left: `${tick.left}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>

      <div className="admin-sessions-chart__rows">
        {visibleRows.map((row) => (
          <div key={row.username} className="admin-sessions-chart__row">
            <div className="admin-sessions-chart__label" title={row.username}>
              {isSingleUser ? 'Сессии' : row.username}
            </div>
            <div className="admin-sessions-chart__track">
              {row.bars.map((bar) => (
                <div
                  key={bar.id}
                  className="admin-sessions-chart__bar-wrap"
                  style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                >
                  <Tooltip
                    title={
                      <>
                        {!isSingleUser && <div>{row.username}</div>}
                        <div>С {formatTime(bar.start)}</div>
                        <div>До {formatTime(bar.end)}</div>
                        <div>Длительность: {formatDurationSec(bar.durationSec)}</div>
                        {bar.ip && <div>IP: {bar.ip}</div>}
                      </>
                    }
                  >
                    <div className="admin-sessions-chart__bar" />
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="admin-sessions-chart__legend">
        <span><i className="dot dot--session" /> Подключение к сервису</span>
        <span className="admin-sessions-chart__hint">
          {layout.totalSessions} сессий · {layout.userRows.length}{' '}
          {layout.userRows.length === 1 ? 'пользователь' : layout.userRows.length < 5 ? 'пользователя' : 'пользователей'}
          · {totalHours} ч суммарно
          {hiddenUsers > 0 && ` · ещё ${hiddenUsers} в таблице «Сводка по пользователям»`}
        </span>
      </div>
    </div>
  );
};

export default WorkSessionsTimeline;
